/* Flying Colors, drawer navigation
   Dipakai oleh: snippets/fc-drawer-nav.liquid, fc-drawer-nav-mobile.liquid

   Gerak diadaptasi dari Osmo Supply, "Mega Nav Directional Hover":
     - hover intent dua timer, 120ms masuk dan 150ms keluar, supaya tidak
       berkedip saat kursor cuma lewat
     - container morph tinggi ke tinggi panel yang diukur on-the-fly
     - pergantian pane memakai gerakan yang sama persis dengan animasi buka,
       yaitu tiap baris naik dari bawah satu per satu. Sengaja BUKAN geser
       kiri kanan: dua gerakan berbeda di satu menu terbaca tidak nyambung.
     - konten masuk dengan stagger

   GSAP sudah ada di theme (assets/animations.min.js, 3.12.5), jadi tidak ada
   dependensi baru. Kalau GSAP tidak ada, semuanya jatuh ke transisi CSS di
   fc-drawer-nav.css dan tetap berfungsi, hanya tanpa morph dan arah.

   Tanpa JavaScript sama sekali, drawer tetap tertutup lewat atribut hidden
   dari Liquid. Tidak ada yang rusak.
*/

(function () {
  'use strict';

  /* Nilai dari note Osmo, dipertahankan supaya mudah dibandingkan. */
  var DUR = {
    bgMorph: 0.4,
    contentIn: 0.3,
    contentOut: 0.2,
    stagger: 0.25,
    /* Pergantian pane. Isi lama keluar berbarengan sambil naik sedikit, isi
       baru masuk satu per satu dari atas ke bawah sambil naik dari bawah,
       persis seperti animasi buka. Stagger per baris inilah gerakan yang
       dipilih, jadi jangan diganti jadi satu blok utuh, dan jangan diganti
       jadi geser kiri kanan.

       Bedanya dengan versi paling awal cuma satu, yaitu masuknya mulai sedikit
       sebelum keluarnya selesai (paneOverlap), jadi tidak ada jeda kosong di
       tengah. Total tetap sekitar 0,76 detik. */
    paneOut: 0.22,
    paneIn: 0.34,
    paneOverlap: 0.16
  };

  /* Jarak naik tiap baris saat berpindah pane, dalam piksel. Angkanya sama
     dengan animasi buka (y: 10) supaya kedua gerakan terasa satu keluarga. */
  var PANE_RISE = 10;

  /* Elemen di dalam pane yang ikut bergerak satu per satu. */
  var BITS = 'li, .fc-drawer__pane-title, .fc-drawer__button, .fc-drawer__footlinks';

  function bitsOf(pane) {
    return pane ? pane.querySelectorAll(BITS) : [];
  }
  var HOVER_ENTER = 120;
  var HOVER_LEAVE = 150;

  /* Jeda sebelum kategori berganti. Tanpa ini, kursor yang menyapu rail dari
     atas ke bawah melewati tujuh kategori dan memicu tujuh pergantian
     beruntun, masing-masing memotong yang sebelumnya, jadi animasinya tidak
     pernah sempat jalan dan terlihat seperti tidak ada animasi sama sekali. */
  var PANE_ENTER = 90;

  var FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  function focusables(root) {
    return Array.prototype.filter.call(root.querySelectorAll(FOCUSABLE), function (el) {
      return el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement;
    });
  }

  function hasGsap() {
    return typeof window.gsap !== 'undefined' && typeof window.gsap.timeline === 'function';
  }

  function reducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function animated() {
    return hasGsap() && !reducedMotion();
  }

  function staggerFor(n) {
    return n <= 1 ? 0 : { amount: DUR.stagger };
  }

  /* Tinggi kolom pane dikunci ke nilai tertinggi, dihitung sekali per lebar
     layar. Yang diukur adalah tinggi baris grid saat tiap pane aktif, bukan
     tinggi isi pane sendirian, karena kolom pane ikut meregang mengikuti rail.
     Jadi angkanya memang tinggi dialog untuk pane itu.

     Tanpa ini tinggi dialog naik turun tiap ganti kategori dan perpindahannya
     terlihat melompat. Di menu sekarang hasilnya 804 untuk sembilan pane, dan
     848 untuk Bird Houses yang punya 15 baris. */
  function lockPaneHeight(drawer) {
    var box = drawer.querySelector('.fc-drawer__panes');
    if (!box) return;

    var key = String(window.innerWidth);
    if (box.dataset.fcLocked === key) return;

    var panes = Array.prototype.slice.call(box.querySelectorAll('[data-fc-pane]'));
    if (!panes.length) return;

    var was = panes.map(function (p) { return p.hidden; });
    box.style.height = '';

    var max = 0;
    panes.forEach(function (p) {
      panes.forEach(function (q) { q.hidden = q !== p; });
      var h = box.getBoundingClientRect().height;
      if (h > max) max = h;
    });

    panes.forEach(function (p, i) { p.hidden = was[i]; });
    box.style.height = Math.ceil(max) + 'px';
    box.dataset.fcLocked = key;
  }

  /* ------------------------------------------------------------- desktop */

  if (typeof customElements !== 'undefined' && !customElements.get('fc-drawer-nav')) {
    customElements.define(
      'fc-drawer-nav',
      class extends HTMLElement {
        connectedCallback() {
          this.openKey = null;
          this.lastTrigger = null;
          this.hoverTimer = null;
          this.leaveTimer = null;
          this.paneTimer = null;
          this.paneKey = null;
          this.tl = null;

          this.dataset.menuStatus = 'closed';
          /* Kalau GSAP menyetir, transisi CSS dimatikan supaya tidak dobel. */
          if (animated()) this.classList.add('fc-nav--gsap');
          Array.prototype.forEach.call(this.querySelectorAll('[data-fc-drawer]'), function (d) {
            d.hidden = false;
          });
          Array.prototype.forEach.call(this.querySelectorAll('[data-fc-drawer-toggle]'), function (t) {
            t.dataset.dropdownToggle = 'closed';
          });

          this.onClick = this.onClick.bind(this);
          this.onKeydown = this.onKeydown.bind(this);
          this.onDocClick = this.onDocClick.bind(this);
          this.onOver = this.onOver.bind(this);
          this.onOut = this.onOut.bind(this);

          this.addEventListener('click', this.onClick);
          this.addEventListener('mouseover', this.onOver);
          this.addEventListener('mouseout', this.onOut);
          document.addEventListener('keydown', this.onKeydown);
          document.addEventListener('click', this.onDocClick);
        }

        disconnectedCallback() {
          this.clearTimers();
          document.removeEventListener('keydown', this.onKeydown);
          document.removeEventListener('click', this.onDocClick);
        }

        clearTimers() {
          clearTimeout(this.hoverTimer);
          clearTimeout(this.leaveTimer);
          clearTimeout(this.paneTimer);
          this.hoverTimer = this.leaveTimer = this.paneTimer = null;
        }

        killTl() {
          if (this.tl) {
            this.tl.kill();
            this.tl = null;
          }
        }

        /* Transisi yang masih jalan diselesaikan, bukan dibunuh. Kalau dibunuh
           di tengah jalan, pane lama tidak sempat dapat atribut hidden dan dua
           pane ikut terlihat pada pembukaan berikutnya. */
        settleTl() {
          if (this.tl) {
            this.tl.progress(1);
            this.tl.kill();
            this.tl = null;
          }
        }

        /* --------------------------------------------------- hover intent */

        onOver(event) {
          var self = this;

          var toggle = event.target.closest('[data-fc-drawer-toggle]');
          if (toggle && this.contains(toggle)) {
            clearTimeout(this.leaveTimer);
            clearTimeout(this.hoverTimer);
            var key = toggle.getAttribute('data-fc-drawer-toggle');
            if (this.openKey === key) return;
            this.hoverTimer = setTimeout(function () {
              self.open(key, toggle);
            }, HOVER_ENTER);
            return;
          }

          /* Ganti kategori juga lewat hover, bukan klik, tapi pakai jeda
             pendek supaya kursor yang cuma lewat tidak memicu pergantian. */
          var pane = event.target.closest('[data-fc-pane-toggle]');
          if (pane && this.contains(pane)) {
            clearTimeout(this.leaveTimer);
            clearTimeout(this.paneTimer);
            var pkey = pane.getAttribute('data-fc-pane-toggle');
            if (this.paneKey === pkey) return;
            this.paneTimer = setTimeout(function () {
              self.showPane(pkey);
            }, PANE_ENTER);
            return;
          }

          /* Kursor masuk ke badan drawer, batalkan rencana menutup. */
          if (event.target.closest('[data-fc-drawer]')) {
            clearTimeout(this.leaveTimer);
          }
        }

        onOut(event) {
          var self = this;
          if (!this.openKey) return;
          /* Abaikan perpindahan di dalam nav yang sama. */
          if (event.relatedTarget && this.contains(event.relatedTarget)) return;
          clearTimeout(this.hoverTimer);
          this.leaveTimer = setTimeout(function () {
            self.close();
          }, HOVER_LEAVE);
        }

        /* --------------------------------------------------------- klik */

        onClick(event) {
          var toggle = event.target.closest('[data-fc-drawer-toggle]');
          if (toggle && this.contains(toggle)) {
            event.preventDefault();
            this.clearTimers();
            var key = toggle.getAttribute('data-fc-drawer-toggle');
            if (this.openKey === key) {
              this.close();
            } else {
              this.open(key, toggle);
            }
            return;
          }

          if (event.target.closest('[data-fc-drawer-close]')) {
            event.preventDefault();
            this.close();
            return;
          }

          /* Klik tetap dilayani untuk keyboard dan layar sentuh. */
          var pane = event.target.closest('[data-fc-pane-toggle]');
          if (pane && this.contains(pane)) {
            event.preventDefault();
            clearTimeout(this.paneTimer);
            this.showPane(pane.getAttribute('data-fc-pane-toggle'));
          }
        }

        onKeydown(event) {
          if (!this.openKey) return;

          if (event.key === 'Escape') {
            this.close();
            return;
          }

          if (event.key !== 'Tab') return;

          var drawer = this.querySelector('[data-fc-drawer="' + this.openKey + '"]');
          if (!drawer) return;
          var items = focusables(drawer);
          if (!items.length) return;

          var first = items[0];
          var last = items[items.length - 1];

          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
          }
        }

        onDocClick(event) {
          if (!this.openKey) return;
          if (this.contains(event.target)) return;
          this.close();
        }

        /* --------------------------------------------------- buka, tutup */

        open(key, trigger) {
          this.clearTimers();
          this.settleTl();
          this.reset();

          var drawer = this.querySelector('[data-fc-drawer="' + key + '"]');
          if (!drawer) return;

          drawer.classList.add('is-visible');
          this.dataset.menuStatus = 'open';

          if (trigger) {
            trigger.setAttribute('aria-expanded', 'true');
            trigger.dataset.dropdownToggle = 'open';
            this.lastTrigger = trigger;
          }
          this.openKey = key;

          if (!animated()) return;

          var inner = drawer.querySelector('.fc-drawer__inner');
          if (!inner) return;

          /* Dikunci setelah drawer terlihat, karena elemen tersembunyi tidak
             punya tinggi untuk diukur. */
          lockPaneHeight(drawer);

          var shown = drawer.querySelector('[data-fc-pane]:not([hidden])');
          this.paneKey = shown ? shown.getAttribute('data-fc-pane') : null;

          var target = inner.getBoundingClientRect().height;
          var pane = drawer.querySelector('[data-fc-pane]:not([hidden])');
          var bits = pane ? pane.querySelectorAll('li, .fc-drawer__pane-title, .fc-drawer__button, .fc-drawer__footlinks') : [];

          var tl = window.gsap.timeline({
            onComplete: function () {
              window.gsap.set(inner, { height: 'auto' });
            }
          });
          this.tl = tl;

          tl.fromTo(inner, { height: 0 }, { height: target, duration: DUR.bgMorph, ease: 'power3.out' }, 0);
          tl.fromTo(drawer, { autoAlpha: 0, y: -8 }, { autoAlpha: 1, y: 0, duration: DUR.contentIn, ease: 'power2.out' }, 0);
          if (bits.length) {
            tl.fromTo(
              bits,
              { autoAlpha: 0, y: 10 },
              { autoAlpha: 1, y: 0, duration: DUR.contentIn, stagger: staggerFor(bits.length), ease: 'power2.out' },
              DUR.contentOut * 0.5
            );
          }
        }

        close() {
          this.clearTimers();
          this.settleTl();
          /* Pembersihan inline style GSAP dikerjakan reset(), untuk semua
             drawer sekaligus, jadi tidak diulang di sini. */
          this.reset();
          this.openKey = null;
          this.dataset.menuStatus = 'closed';
          if (this.lastTrigger) {
            this.lastTrigger = null;
          }
        }

        reset() {
          Array.prototype.forEach.call(this.querySelectorAll('[data-fc-drawer]'), function (d) {
            d.classList.remove('is-visible');

            /* Melepas class saja TIDAK cukup. GSAP menulis opacity dan
               visibility sebagai inline style, dan inline mengalahkan
               stylesheet, jadi drawer yang lama tetap terlihat waktu drawer
               lain dibuka dan dua mega menu tampil bersamaan. Inline-nya harus
               ikut dibersihkan di sini, bukan cuma di close(). */
            if (hasGsap()) {
              window.gsap.set(d, { clearProps: 'all' });
              var box = d.querySelector('.fc-drawer__inner');
              if (box) window.gsap.set(box, { clearProps: 'height' });
            }
          });
          Array.prototype.forEach.call(this.querySelectorAll('[data-fc-drawer-toggle]'), function (t) {
            t.setAttribute('aria-expanded', 'false');
            t.dataset.dropdownToggle = 'closed';
          });
        }

        /* ------------------------------------ ganti pane, arah mengikuti */

        showPane(key) {
          var target = this.querySelector('[data-fc-pane="' + key + '"]');
          if (!target) return;

          var container = target.parentElement;
          var panes = Array.prototype.slice.call(container.querySelectorAll('[data-fc-pane]'));

          var markToggles = function (scope) {
            var rail = container.parentElement;
            Array.prototype.forEach.call(rail.querySelectorAll('[data-fc-pane-toggle]'), function (b) {
              b.setAttribute('aria-expanded', b.getAttribute('data-fc-pane-toggle') === scope ? 'true' : 'false');
            });
          };

          if (!animated()) {
            panes.forEach(function (p) { p.hidden = p !== target; });
            this.paneKey = key;
            markToggles(key);
            return;
          }

          /* Pane aktif dibaca dari tujuan transisi terakhir, bukan dari yang
             kebetulan belum hidden, karena selama transisi ada dua yang tampil. */
          var current = this.paneKey ? this.querySelector('[data-fc-pane="' + this.paneKey + '"]') : null;
          if (!current || current.hidden) {
            current = panes.filter(function (p) { return !p.hidden; })[0];
          }

          if (!current) {
            panes.forEach(function (p) { p.hidden = p !== target; });
            this.paneKey = key;
            markToggles(key);
            return;
          }

          if (current === target) {
            this.paneKey = key;
            markToggles(key);
            return;
          }

          /* Transisi lama dihentikan di tempat, TIDAK dilompatkan ke akhir.
             Melompat ke akhir itu yang dulu terlihat sebagai patahan. Dengan
             dihentikan di tempat, tween berikutnya berangkat dari opacity dan
             posisi yang sedang berjalan, jadi sambungannya mulus. */
          this.killTl();

          /* Sisa pane dari transisi yang terpotong dibereskan. */
          panes.forEach(function (p) {
            if (p === current || p === target) return;
            if (!p.hidden) {
              p.hidden = true;
              window.gsap.set(p, { clearProps: 'all' });
              window.gsap.set(bitsOf(p), { clearProps: 'all' });
            }
          });

          var fromBits = bitsOf(current);
          var toBits = bitsOf(target);

          /* Kalau target masih tersembunyi, isinya ditaruh dulu di posisi
             berangkat. Kalau ternyata sudah tampil karena transisi sebelumnya
             terpotong, tween di bawah berangkat dari keadaannya sekarang, jadi
             tidak berkedip. */
          if (target.hidden) {
            target.hidden = false;
            window.gsap.set(target, { clearProps: 'all' });
            if (toBits.length) {
              window.gsap.set(toBits, { autoAlpha: 0, y: PANE_RISE });
            }
          }

          this.paneKey = key;
          markToggles(key);

          var tl = window.gsap.timeline({
            onComplete: function () {
              current.hidden = true;
              window.gsap.set([current, target], { clearProps: 'all' });
              if (fromBits.length) window.gsap.set(fromBits, { clearProps: 'all' });
              if (toBits.length) window.gsap.set(toBits, { clearProps: 'all' });
            }
          });
          this.tl = tl;

          /* Isi lama pergi berbarengan. */
          if (fromBits.length) {
            tl.to(
              fromBits,
              { autoAlpha: 0, y: -PANE_RISE, duration: DUR.paneOut, ease: 'power2.in' },
              0
            );
          }

          /* Isi baru datang satu per satu, dari atas ke bawah. */
          if (toBits.length) {
            tl.to(
              toBits,
              {
                autoAlpha: 1,
                y: 0,
                duration: DUR.paneIn,
                stagger: staggerFor(toBits.length),
                ease: 'power2.out'
              },
              DUR.paneOverlap
            );
          }
        }
      }
    );
  }

  /* -------------------------------------------------------------- mobile */

  function initMobile(root) {
    if (!root || root.dataset.fcMnavReady === 'true') return;
    root.dataset.fcMnavReady = 'true';

    /* Penanda bahwa GSAP yang menyetir, dipakai CSS untuk mempersempit daftar
       transisi anchor. Sama peranannya dengan .fc-nav--gsap di sisi desktop. */
    if (animated()) root.classList.add('fc-mnav--gsap');

    function show(key, dir) {
      var panes = Array.prototype.slice.call(root.querySelectorAll('[data-fc-mpane]'));
      var target = root.querySelector('[data-fc-mpane="' + key + '"]');
      if (!target) return;

      panes.forEach(function (p) {
        var mine = p === target;
        p.hidden = !mine;
        p.classList.toggle('is-current', mine);
      });
      root.scrollTop = 0;

      if (animated()) {
        var bits = target.querySelectorAll('li, .fc-mnav__title, .fc-mnav__button, .fc-mnav__back');
        if (bits.length) {
          window.gsap.fromTo(
            bits,
            { autoAlpha: 0, x: dir * 24 },
            { autoAlpha: 1, x: 0, duration: DUR.contentIn, stagger: staggerFor(bits.length), ease: 'power2.out', clearProps: 'all' }
          );
        }
      }

      var focusTarget = target.querySelector('[data-fc-mback]') || focusables(target)[0];
      if (focusTarget) focusTarget.focus();
    }

    var rootPane = root.querySelector('[data-fc-mpane="root"]');
    if (rootPane) rootPane.classList.add('is-current');

    root.addEventListener('click', function (event) {
      var open = event.target.closest('[data-fc-mopen]');
      if (open) {
        event.preventDefault();
        show(open.getAttribute('data-fc-mopen'), 1);
        return;
      }
      if (event.target.closest('[data-fc-mback]')) {
        event.preventDefault();
        show('root', -1);
      }
    });
  }

  /* Isi laci masuk satu per satu saat laci dibuka, pola dari Osmo Side
     Navigation Wipe Effect: panel bergerak dulu, baru daftar link menyusul
     dengan stagger. Di note aslinya link mulai di "<+=0.35" dengan stagger
     0.05, jadi angka di bawah mengikuti itu.

     Gerakannya sengaja sama dengan animasi drawer desktop, yaitu naik dari
     bawah (y 10 ke 0), supaya seluruh navigasi terasa satu bahasa.

     Dipantau lewat MutationObserver pada kelas .active milik wrapper, bukan
     dengan membajak klik tombol burger, supaya logika buka tutup tetap milik
     header.js dan tidak ada dua penangan yang saling rebut. */
  function initMobileReveal() {
    var wrap = document.querySelector('.mobile-toggle-wrapper');
    if (!wrap || wrap.dataset.fcRevealReady === 'true') return;
    if (!wrap.querySelector('[data-fc-mnav]')) return;
    wrap.dataset.fcRevealReady = 'true';

    var reveal = function () {
      if (!animated()) return;
      var mnav = wrap.querySelector('[data-fc-mnav]');
      if (!mnav) return;
      var pane = Array.prototype.filter.call(
        mnav.querySelectorAll('[data-fc-mpane]'),
        function (p) { return !p.hidden; }
      )[0];
      if (!pane) return;

      var bits = pane.querySelectorAll('li, .fc-mnav__shopall, .fc-mnav__title, .fc-mnav__button, .fc-mnav__back');
      if (!bits.length) return;

      window.gsap.fromTo(
        bits,
        { autoAlpha: 0, y: 10 },
        {
          autoAlpha: 1,
          y: 0,
          duration: 0.34,
          stagger: 0.05,
          delay: 0.2,
          ease: 'power2.out',
          clearProps: 'all'
        }
      );
    };

    new MutationObserver(function () {
      if (wrap.classList.contains('active')) reveal();
    }).observe(wrap, { attributes: true, attributeFilter: ['class'] });
  }

  /* Klik overlay menutup laci. Urutannya menyalin header.js supaya konsisten:
     lepas overflow-hidden di body, lepas kelas active agar animasi keluar jalan,
     lalu cabut atribut open setelah 400ms, sama dengan closeAnimation di sana. */
  document.addEventListener('click', function (event) {
    var overlay = event.target.closest && event.target.closest('[data-fc-mnav-overlay]');
    if (!overlay) return;

    var wrap = overlay.closest('.mobile-toggle-wrapper');
    if (!wrap) return;

    document.body.classList.remove('overflow-hidden');
    wrap.classList.remove('active');
    setTimeout(function () {
      wrap.removeAttribute('open');
    }, 400);
  });

  function initAllMobile() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-fc-mnav]'), initMobile);
    initMobileReveal();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllMobile);
  } else {
    initAllMobile();
  }

  document.addEventListener('shopify:section:load', initAllMobile);
})();
